import { CSSProperties, ReactNode } from 'react';

import { PointItem } from 'components/point-item';
import { faseDoItem } from 'components/point-item/fase';
import { FormatNumber } from 'utils/helpers';

import IlustracaoRealizacao from 'assets/img/realizacao-destaque-equipe.png';

import { maiorBonificacao, pontosPorFase } from '../../perfil-calculos';
import { TITULO_DE_SECAO } from 'styles';
import SemRegistros from '../SemRegistros';

interface RealizacaoDestaqueProps {
  itens: any[];
  totalDoCiclo?: number;
  cicloLabel?: string;
  /** Linha do tempo antiga dos ciclos 2S2023 e 1S2024, que a página ainda mostra. */
  linhaDoTempoAntiga?: ReactNode;
}

export function Indicador({ rotulo, valor }: { rotulo: string; valor: string | number }) {
  return (
    <div className="min-w-[132px] rounded-xl bg-surface-0 px-5 py-3.5 shadow-jj-01">
      <div className="mb-1 text-xs text-content-soft">{rotulo}</div>
      <div className="font-display text-[26px] font-extrabold leading-none text-brand-blue">{valor}</div>
    </div>
  );
}

/** A barra escala sobre a área sem o número (20px de linha mais 8px até a barra), na proporção da fase. */
const ALTURA_DA_BARRA = 'h-[calc((100%_-_28px)*var(--proporcao))]';

interface GraficoDePontosPorFaseProps {
  itens: any[];
  rotulo: string;
  /** Altura, fundo e espaçamento do cartão onde o gráfico entra. */
  className: string;
}

/** Pontos de Realização Destaque por fase, agrupados por `faseDoItem`: a aba e o bloco do Resumo (AJ2-34). */
export function GraficoDePontosPorFase({ itens, rotulo, className }: GraficoDePontosPorFaseProps) {
  return (
    <ul aria-label={rotulo} className={`m-0 flex min-w-0 list-none items-end gap-3 ${className}`}>
      {pontosPorFase(itens, faseDoItem).map(({ fase, pontos, altura, destaque }) => (
        <li key={fase} className="flex h-full min-w-0 flex-1 flex-col items-center gap-2">
          {/* Número e barra dividem a área acima do rótulo, e a barra escala só sobre o que sobra do número
              (28px). Com a altura sobre a coluna inteira, a fase de 100% empurrava o rótulo para fora do
              cartão e cortava "Março" (AJH-14). */}
          <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-end gap-2">
            <span className="font-display text-sm font-bold leading-5 text-brand-blue">{FormatNumber(pontos, false)}</span>
            <div
              data-barra
              className={`${ALTURA_DA_BARRA} w-full shrink-0 rounded-t-lg ${destaque ? 'bg-brand-blue' : 'bg-brand-cyan-light'}`}
              style={{ '--proporcao': altura / 100 } as CSSProperties}
            />
          </div>
          <span className="truncate text-xs font-semibold capitalize leading-normal text-content">{fase}</span>
        </li>
      ))}
    </ul>
  );
}

export default function RealizacaoDestaque({ itens, totalDoCiclo, cicloLabel, linhaDoTempoAntiga }: RealizacaoDestaqueProps) {
  if (itens.length === 0) return <SemRegistros />;

  return (
    <div className="flex flex-col gap-[22px]">
      <section
        aria-labelledby="titulo-realizacoes"
        className="flex flex-wrap items-center gap-7 rounded-2xl bg-brand-green px-6 py-[26px] lg:px-[30px]"
      >
        <div className="min-w-0 flex-1">
          <h2 id="titulo-realizacoes" className="m-0 mb-1 font-display text-xl font-bold text-brand-blue-dark">
            Suas Realizações Destaque
          </h2>
          <p className="m-0 mb-5 max-w-[520px] text-sm text-content">
            Todas as realizações apreciadas e bonificadas pela sua liderança no ciclo {cicloLabel}.
          </p>
          <div className="flex flex-wrap gap-3.5">
            <Indicador rotulo="Total do ciclo" valor={FormatNumber(totalDoCiclo ?? 0, false)} />
            <Indicador rotulo="Realizações" valor={itens.length} />
            <Indicador rotulo="Maior bonificação" valor={FormatNumber(maiorBonificacao(itens), false)} />
          </div>
        </div>
        {/* Ilustração da equipe (task-mv8dqqets2ky, AJH-58): 335 × 360, mostrada a 180px de altura (2x). */}
        <img src={IlustracaoRealizacao} alt="" className="hidden h-[180px] w-auto shrink-0 self-center md:block" />
      </section>

      {linhaDoTempoAntiga}

      <section>
        <h2 className={`${TITULO_DE_SECAO} mb-3`}>Pontos por fase</h2>
        <GraficoDePontosPorFase
          itens={itens}
          rotulo="Pontos por fase"
          className="h-[170px] rounded-2xl bg-surface-0 px-5 py-6 shadow-jj-01 lg:gap-[22px] lg:px-7"
        />
      </section>

      <section>
        <h2 className={`${TITULO_DE_SECAO} mb-3`}>
          Linha do tempo <span className="text-brand-blue">({itens.length})</span>
        </h2>
        <div className="flex flex-col gap-3">
          {itens.map(item => (
            <PointItem key={item._id} data={item} />
          ))}
        </div>
      </section>
    </div>
  );
}
